import m, {Vnode, VnodeDOM} from "mithril";
import {
    bindLightboxes,
    destroyLightboxes,
    LightboxBinding,
} from "../../../../extensions/Lightbox/js/LightboxBindings";
import {TInterviewMessage} from "../model/TInterviewThread";
import {tokenizeInterviewGoalAttachments} from "./InterviewGoalAttachmentMarker";
import {
    renderInterviewGoalAttachment,
    renderInterviewGoalAttachmentGallery,
} from "./InterviewGoalAttachmentView";

export type TInterviewMessageContentAttrs = {
    message: TInterviewMessage,
    className?: string,
};

export function renderInterviewMessageContent(message: TInterviewMessage): Vnode|string|(Vnode|string)[] {
    if (message.role === "agent" && message.contentFormat === "html") {
        return m.trust(message.content);
    }
    if (message.role !== "agent" || message.contentFormat !== "plain") {
        return message.content;
    }
    if (
        (message.goalAttachments ?? []).length === 0
        && !message.content.includes("[[interview-goal-attachment:")
    ) {
        return message.content;
    }

    const galleryId = `interview-message-${message.id}`;
    return tokenizeInterviewGoalAttachments(message).map(segment => {
        if (segment.type === "text") {
            return segment.content;
        }
        if (segment.type === "gallery") {
            return renderInterviewGoalAttachmentGallery(segment.attachments, galleryId);
        }

        return renderInterviewGoalAttachment(segment.attachment, galleryId);
    });
}

export default class InterviewMessageContentView {

    private lightboxBindings: LightboxBinding[] = [];

    public oncreate(vnode: VnodeDOM<TInterviewMessageContentAttrs>): void {
        this.lightboxBindings = bindLightboxes($(vnode.dom));
    }

    public onbeforeupdate(
        vnode: Vnode<TInterviewMessageContentAttrs>,
        old: Vnode<TInterviewMessageContentAttrs>,
    ): boolean {
        if (
            vnode.attrs.message === old.attrs.message
            && vnode.attrs.className === old.attrs.className
        ) {
            return false;
        }

        this.destroyLightboxes();
        return true;
    }

    public onupdate(vnode: VnodeDOM<TInterviewMessageContentAttrs>): void {
        this.lightboxBindings = bindLightboxes($(vnode.dom));
    }

    public onremove(): void {
        this.destroyLightboxes();
    }

    public view(vnode: Vnode<TInterviewMessageContentAttrs>): Vnode {
        const {message, className = ""} = vnode.attrs;
        const richClass = message.role === "agent" && message.contentFormat === "html"
            ? "interview-thread-message-content-rich"
            : "";

        return m(".interview-message-content-view", {
            className: `${className} ${richClass}`.trim(),
        }, renderInterviewMessageContent(message));
    }

    private destroyLightboxes(): void {
        destroyLightboxes(this.lightboxBindings);
        this.lightboxBindings = [];
    }
}
